import React, { useCallback, useMemo, useState } from 'react';

import Button from '@adsk/alloy-react-button';
import Dropdown, { VirtualizedMenu } from '@adsk/alloy-react-dropdown';
import FormField from '@adsk/alloy-react-form-field';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import Panel from '@adsk/alloy-react-panel';
import TextInput from '@adsk/alloy-react-text-input';
import TextArea from '@adsk/alloy-react-textarea';

import { useCostContext } from './CostProvider';
import { useTranslation } from './i18n';
import { ERROR_CODE } from '../helpers/consts';
import costService from '../helpers/costSdk';
import { customizedType } from '../helpers/localeHelper';

const Create = ({
  projectId,
  onClose,
  open,
  floatingPanel,
  onCreateSuccess,
  sourceEntityType,
  sourceEntityNumber,
  initialFields,
  type = '',
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotifications();
  const { terminologies, contracts, isPcoCollaborateCreate } = useCostContext();
  const { name: initialName, description: initialDescription } =
    initialFields || {};

  const [name, setName] = useState(initialName);
  const [contract, setContract] = useState(null);
  const [description, setDescription] = useState(initialDescription);
  const [isCreating, setIsCreating] = useState(false);

  const showContract = useMemo(
    () => type === 'pco' && isPcoCollaborateCreate,
    [isPcoCollaborateCreate, type],
  );

  const handleOnClose = useCallback(() => {
    setName(initialName);
    setDescription(initialDescription);
    setContract(null);
    onClose();
  }, [onClose, initialName, initialDescription]);

  const handleOnCreate = async () => {
    try {
      setIsCreating(true);
      const entity = await costService.create(
        projectId,
        {
          name,
          contractId: contract?.id,
          description,
          sourceType: sourceEntityType && sourceEntityType.toUpperCase(),
          sourceRef: sourceEntityNumber,
        },
        type,
      );
      onCreateSuccess(entity);
      setIsCreating(false);
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: t('CREATE_SUCCESS', {
          name: `${entity.number}-${entity.name}`,
          type: customizedType(t, type, terminologies),
        }),
      });
      handleOnClose();
    } catch (err) {
      console.error(err);
      const isNoBudgetOnContract =
        err?.error?.errors?.[0]?.code === ERROR_CODE.NO_BUDGET_ON_CONTRACT;
      setIsCreating(false);
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: t(
          isNoBudgetOnContract
            ? 'CREATE_FAILED_NO_BUDGET_ON_CONTRACT'
            : 'CREATE_FAILED',
          {
            contract: t('CONTRACT'),
            pco: t('PCO'),
            type: customizedType(t, type, terminologies),
          },
        ),
      });
    }
  };

  const handleNameChange = useCallback((e) => {
    setName(e.target.value);
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
  }, []);

  const handleContractChange = useCallback(({ selectedItem }) => {
    setContract(selectedItem);
  }, []);

  const contractOptions = useMemo(() => {
    return (
      contracts?.map((contract) => ({
        label: `${contract.code} - ${contract.name}`,
        value: contract.id,
        ...contract,
      })) || []
    );
  }, [contracts]);

  return (
    <Panel
      onClose={handleOnClose}
      title={t('CREATE_CHANGE_ORDER', {
        type: customizedType(t, type, terminologies),
      })}
      open={open}
      floating={floatingPanel}
      renderFooter={({ style }) => (
        <div style={style}>
          <Button
            size={Button.SIZES.LARGE}
            variant={Button.VARIANTS.SECONDARY}
            onClick={handleOnCreate}
            disabled={
              !(name && name.trim()) ||
              (showContract && !contract?.id) ||
              isCreating
            }
            style={{
              width: '100%',
            }}
          >
            {t('CREATE')}
          </Button>
        </div>
      )}
    >
      <Panel.Body>
        <FormField
          label={t('NAME')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ marginBottom: 16 }}
          required
        >
          <TextInput value={name} onChange={handleNameChange} />
        </FormField>
        {showContract && (
          <FormField
            label={t('CONTRACT')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
            style={{ marginBottom: 16 }}
            required
          >
            <Dropdown
              isAutocomplete
              style={{
                width: '100%',
              }}
              items={contractOptions}
              renderMenu={VirtualizedMenu.render}
              onSelectedItemChange={handleContractChange}
            />
          </FormField>
        )}
        <FormField
          label={t('DESCRIPTION')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <TextArea
            minHeight={100}
            value={description}
            onChange={handleDescriptionChange}
            style={{
              width: '100%',
            }}
          />
        </FormField>
      </Panel.Body>
    </Panel>
  );
};

Create.displayName = 'Create';

export default Create;
